import React, { useEffect, useRef, useState } from 'react';
import { Accidental, Formatter, Renderer, Stave, StaveNote, Voice } from 'vexflow';
import type { StaffNote } from '../../types/payloads/melody_staff.types';
import { ensureSalaVexFonts } from './vexFonts';
import { pitchToVexKey, tokenToVexDuration } from './vexFigureMap';
import { buildStaffSvgFragment } from '../staffRender';
import { fitHostSvgToStave } from './vexSvgFit';

interface VexStaffFragmentProps {
  notes: StaffNote[];
  clef?: 'treble' | 'bass';
  timeSignature?: string;
  highlightNoteId?: string;
  width?: number;
  printMode?: boolean;
  /** compact reduce altura para ítems mecánicos (qué nota / línea). */
  density?: 'compact' | 'comfortable';
  className?: string;
}

function noteStyle(active: boolean, printMode: boolean) {
  const stroke = printMode ? '#0a0a0a' : active ? '#a78bfa' : '#f4f0e8';
  const fill = printMode ? '#0a0a0a' : active ? '#c4b5fd' : '#f4f0e8';
  return { fillStyle: fill, strokeStyle: stroke };
}

/** Fragmento de pentagrama VexFlow con notas reales. */
export function VexStaffFragment({
  notes,
  clef = 'treble',
  timeSignature = '4/4',
  highlightNoteId,
  width = 300,
  printMode = false,
  density = 'comfortable',
  className = '',
}: VexStaffFragmentProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const compact = density === 'compact' || notes.length <= 4;

  useEffect(() => {
    if (failed || !notes.length) return;
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;

    (async () => {
      try {
        await ensureSalaVexFonts();
        if (cancelled) return;

        host.innerHTML = '';

        const staveX = 10;
        const staveY = compact ? 18 : 32;
        const staveW = Math.max(width - 20, compact ? 220 : 180);
        const canvasW = staveX + staveW + 20;
        const canvasH = compact ? 78 : 110;

        const renderer = new Renderer(host, Renderer.Backends.SVG);
        renderer.resize(canvasW, canvasH);
        const ctx = renderer.getContext();

        const stave = new Stave(staveX, staveY, staveW);
        stave.addClef(clef);
        const [top, bottom] = timeSignature.split('/');
        if (top && bottom) stave.addTimeSignature(`${top}/${bottom}`);
        stave.setContext(ctx);
        stave.draw();

        const vexNotes = notes.map((note) => {
          const active = note.id === highlightNoteId;
          const duration = tokenToVexDuration(note.duration);
          const sn = new StaveNote({
            keys: [pitchToVexKey(note.pitch)],
            duration,
            autoStem: note.duration !== 'W',
          });
          if (note.accidental === 'sharp') sn.addModifier(new Accidental('#'), 0);
          if (note.accidental === 'flat') sn.addModifier(new Accidental('b'), 0);
          sn.setStyle(noteStyle(active, printMode));
          return sn;
        });

        const [n, d] = timeSignature.split('/').map(Number);
        const voice = new Voice({ numBeats: n || 4, beatValue: d || 4 });
        voice.setStrict(false);
        voice.addTickables(vexNotes);
        const formatWidth = Math.max(80, stave.getNoteEndX() - stave.getNoteStartX() - 8);
        new Formatter().joinVoices([voice]).format([voice], formatWidth);
        vexNotes.forEach((note) => {
          note.setStave(stave);
          note.draw();
        });

        fitHostSvgToStave(host, stave, canvasW);

        const svg = host.querySelector('svg');
        if (svg) {
          svg.classList.add('vex-staff-fragment__svg');
          if (printMode) svg.classList.add('vex-staff-fragment__svg--print');
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [notes, clef, timeSignature, highlightNoteId, width, failed, printMode, compact]);

  if (failed) {
    return (
      <div
        className={`vex-staff-fragment-fallback ${className}`.trim()}
        dangerouslySetInnerHTML={{
          __html: buildStaffSvgFragment(notes, { clef, highlightId: highlightNoteId, width }),
        }}
      />
    );
  }

  return (
    <div
      ref={hostRef}
      className={`vex-staff-fragment ${printMode ? 'vex-staff-fragment--print' : ''} ${className}`.trim()}
    />
  );
}
